import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { EllipsisIcon, PlusIcon } from 'lucide-react';

import type { ProjectImageType } from '@/api/media';
import { getMediaOptions } from '@/api/media';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ImageCarousel } from '@/components/ui/image-carousel';
import type { UploadMediaModalType } from '@/features/estate-media/components/upload-media-modal';
import {
  ACCEPTED_IMAGE_INPUT_TYPE,
  uploadImagesSchema,
} from '@/features/estate-media/schema/upload-media';
import { EstateImage } from '@/features/estates-property/components/estate-image';
import { useUploadImage } from '@/features/project-media/api/upload-image';
import { useModal } from '../../../../components/modals/new/use-modal';
import { UploadMediaModal } from '../../../estate-media/components/upload-media-modal';

type ImagesCardProps = {
  data: ProjectImageType[][];
  projectId: string;
  canWriteMedia: boolean;
};

export const ImagesCard = ({ data = [], projectId, canWriteMedia }: ImagesCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const uploadModal = useModal();

  const { mutateAsync: uploadImage } = useUploadImage({
    mutationConfig: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMediaOptions(projectId).queryKey });
        toast.success(t('successfullyAddedImage'));
      },
    },
  });

  const images = data?.flat() || [];
  const amount = images.length;

  const handleImageNavigate = () => {
    navigate({ to: '/projects/$projectId/media/images', params: { projectId: projectId } });
  };

  const submitHandler = async (data: UploadMediaModalType) => {
    const dataTransfer = new DataTransfer();
    data.files.forEach((file) => dataTransfer.items.add(file));
    await uploadImage({ images: dataTransfer.files, projectId });
    uploadModal.dismiss();
  };

  const handleOpenUploadImageModal = () => {
    uploadModal.open();
  };

  return (
    <>
      <Card className="flex flex-col rounded-md">
        <div className="h-52 w-full overflow-hidden">
          {amount === 0 ? (
            <EstateImage className="!h-52 w-full" thumbnailId="default-image-gallery-image" />
          ) : (
            <ImageCarousel imageClassName="size-full object-cover !h-52" images={images} />
          )}
        </div>

        <div className="flex w-full justify-between bg-white px-10 pb-14 pt-7 sm:flex-col">
          <div>
            <h3 className="text-md">{t('images')}</h3>
            <span className="text-base text-text-secondary"> {amount}</span>
          </div>
          <div className="mt-auto flex flex-wrap items-center justify-center gap-2 xl:flex-col">
            <Button className="w-full" variant="contained" onClick={handleImageNavigate}>
              <EllipsisIcon className="max-xl:block xl:hidden" />
              <p className="text-xs max-xl:hidden">{t('seeAllBtn', { value: t('images') })}</p>
            </Button>
            {canWriteMedia ? (
              <Button className="w-full" variant="outline" onClick={handleOpenUploadImageModal}>
                <PlusIcon className="max-xl:block" />
                <p className="max-xl:hidden">{t('add-new', { value: t('images') })}</p>
              </Button>
            ) : null}
          </div>
        </div>
      </Card>
      <UploadMediaModal
        modal={uploadModal}
        submitHandler={submitHandler}
        acceptedType={ACCEPTED_IMAGE_INPUT_TYPE}
        acceptedMediaSchema={uploadImagesSchema()}
      />
    </>
  );
};
